import MuxAudio from '@mux-elements/mux-audio-react';
import React from 'react';
import ReactPlayer from 'react-player';
import { SectionModule } from '../../../../api/models/BibleStudyModule';
import { getMuxHlsPlayback } from '../../../../util/mux-utilities';
import { RhButton, RhButtonMode } from '../../../core/RhButton';
import { RhImage } from '../../../core/RhImage';
import { RhModal, RhModalProps } from '../../../core/RhModal';
import { H3, H4, H5 } from '../../../core/Styles';
import { ScriptureText } from './ScriptureText';
import MarkdownIt from 'markdown-it';

const md = new MarkdownIt();

interface ViewModuleModalProps extends Omit<RhModalProps, 'children'> {
  module: SectionModule;
}

export const ViewModuleModal = ({ module, ...rest }: ViewModuleModalProps) => {
  return (
    <RhModal {...rest}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-4">
          <div className="rounded-lg w-14 h-14 bg-stone-200"></div>
          <div>
            <H5>{module.moduleType === 'qanda' ? 'Q&A' : module.moduleType}</H5>
            {(module.moduleType === 'document' || module.moduleType === 'image') && <H3>{module.name}</H3>}
            {(module.moduleType === 'video' ||
              module.moduleType === 'scripture' ||
              module.moduleType === 'qanda' ||
              module.moduleType === 'audio' ||
              module.moduleType === 'text') && <H3>{module.title}</H3>}
          </div>
        </div>
        {/* Body */}
        <div>
          {/* Video module */}
          {module.moduleType === 'video' && !!module.muxID && (
            <ReactPlayer url={getMuxHlsPlayback(module.muxID)} controls />
          )}
          {module.moduleType === 'image' && !!module.url && (
            <div className="flex justify-center w-full max-h-[min(70vh,calc(100dvh-12rem))] overflow-auto">
              <RhImage
                src={module.url}
                mode="full"
                style={{
                  maxHeight: 'min(70vh, calc(100dvh - 12rem))',
                  maxWidth: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
            </div>
          )}
          {/* Scripture module */}
          {module.moduleType === 'scripture' && !!module.scriptureIDList?.length && !!module.translationID && (
            <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-2">
              {module.scriptureIDList.map((scriptureIds, i) => (
                <ScriptureText
                  key={`scripture-module-${i}`}
                  scriptureIds={scriptureIds}
                  version={module.translationID!}
                />
              ))}
            </div>
          )}
          {/* Q&A module */}
          {module.moduleType === 'qanda' && (
            <div className="space-y-6 max-h-[55vh] overflow-y-auto pr-2">
              <div className="space-y-4">
                {module.questionAndAnswers?.map((item, i) => (
                  <div key={i}>
                    <H4>Question {i + 1}</H4>
                    <div>{item.question}</div>
                    <div>
                      Answer: <strong>{item.answer}</strong>
                    </div>
                  </div>
                ))}
              </div>
              {!!module.translationID && !!module.scriptureIDList?.length && (
                <>
                  <div className="border-b" />
                  <div className="space-y-4">
                    {module.scriptureIDList.map((scriptureIds, i) => (
                      <ScriptureText
                        key={`qanda-scripture-${i}`}
                        scriptureIds={scriptureIds}
                        version={module.translationID!}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {/* Audio module */}
          {module.moduleType === 'audio' && !!module.muxID && (
            <>
              <audio style={{ width: '100%' }} controls src={module.muxID} />
              {/*<MuxAudio style={{ width: '100%' }} playbackId={module.muxID} streamType="on-demand" controls />*/}
            </>
          )}
          {module.moduleType === 'text' && (
            <div className="max-h-[40vh] overflow-y-auto">
              {
                <div
                  // The styling is defined in App.css
                  className="mission-statement"
                  dangerouslySetInnerHTML={{ __html: md.render(module.content) }}
                />
              }
            </div>
          )}
        </div>
        {/* Footer */}
        <div className="flex justify-end">
          <RhButton mode={RhButtonMode.text} title="Close" onClick={rest.onRequestClose} />
        </div>
      </div>
    </RhModal>
  );
};
